/**
 * What a floor asks of you, and when it asks.
 *
 * The floor loop used to be one sentence: here is a map, here are forty
 * enemies scattered across all of it, kill them and a door opens. That works,
 * and it keeps working for about six floors — after which a hundred and ten
 * genuinely different maps have all become the same errand, because the errand
 * never changed and the map was only ever the room it happened in.
 *
 * Two things are wrong with it. The first is spatial: enemies distributed over
 * a whole competitive layout means long stretches with nothing in them, a last
 * survivor to be hunted down, and no sense of having *advanced* through
 * anywhere. The second is that a floor with one verb has no pacing — combat
 * is either happening or being searched for, and neither is a peak or a
 * valley.
 *
 * So the map is cut into zones along the route from where you arrive to where
 * you leave, and the fight is released a zone at a time. What the floor wants
 * is one of three contracts, stated in three words on the HUD and otherwise
 * communicated by the geometry. The player is always being pulled forward.
 *
 * The rule this is built under, which is the one Left 4 Dead's director got
 * right and most of its imitators did not: **the budget is the seed's, and the
 * director never touches it**. It decides when a group is released, which of
 * the legal places it comes from, and how long the quiet before the next one
 * lasts. It does not add enemies because you are winning or weaken them
 * because you are not. A daily seed has to mean the same fight for everybody
 * who plays it, and a difficulty that secretly follows you around is one you
 * can neither learn nor beat.
 */

import type { NavmeshGraph } from 'levelspec';
import type { EnemyType } from './enemies.ts';
import { cost } from './enemies.ts';

/** How big the signal is, in metres. Big enough to fight inside. */
export const HOLD_RADIUS = 7.5;

/** What the floor is asking for. Three words on the HUD, and no more. */
export type Contract = 'sweep' | 'hunt' | 'hold';

export const BRIEF: Record<Contract, string> = {
  sweep: 'CLEAR THE ZONES',
  hunt: 'HUNT THE MARKED TARGET',
  hold: 'HOLD THE SIGNAL',
};

/**
 * A piece of the map big enough to have a fight in.
 *
 * Carved out of the navmesh rather than out of the room list, because rooms
 * are an authoring unit and a fight is not — three small connected rooms are
 * one encounter and one enormous hall is two. What separates them is progress
 * along the route, so a zone is always *somewhere you get to*, and the order
 * they are numbered in is the order you meet them.
 */
export interface Zone {
  id: number;
  /** Navmesh samples inside it. */
  nodes: number[];
  /** Canonical (x, y, z) middle, and how far its samples reach from there. */
  centre: [number, number, number];
  radius: number;
  /**
   * The most open standable spot in the zone.
   *
   * Not the same as the centre, and the difference matters: a centre is the
   * average of the zone's navmesh samples, and the average of an L-shaped room
   * is in the wall between its two arms. Anything the player has to physically
   * stand on — the hold signal above all — goes here instead, because it is a
   * real sample of the mesh and it is the one with the most floor around it.
   */
  heart: [number, number, number];
  /** 0 where you arrive, 1 at the way out. */
  progress: number;
  /** Legal spawn posts that belong to it. */
  posts: [number, number, number][];
}

// -----------------------------------------------------------------------------

/** Hop counts from one sample over the whole reachable mesh. */
function bfs(g: NavmeshGraph, from: number): Int32Array {
  const dist = new Int32Array(g.count).fill(-1);
  if (from < 0) return dist;
  const q = new Int32Array(g.count);
  let head = 0;
  let tail = 0;
  q[tail++] = from;
  dist[from] = 0;
  while (head < tail) {
    const u = q[head++];
    const du = dist[u] + 1;
    for (let k = g.adjOffset[u]; k < g.adjOffset[u + 1]; k++) {
      const v = g.adjList[k];
      if (dist[v] !== -1) continue;
      dist[v] = du;
      q[tail++] = v;
    }
  }
  return dist;
}

/**
 * Cut the map into zones along the route.
 *
 * The measure is *flow distance* — how far along you are between arriving and
 * leaving, as a fraction — rather than metres from anything. Two rooms the
 * same distance from the spawn as the crow flies can be one step and forty
 * steps apart on foot, and it is the second number that decides whether they
 * are the same fight.
 *
 * Within a band the largest connected piece wins and the rest is given away to
 * whichever neighbour will take it. A band that is two disconnected halves is
 * a zone the player can "clear" while standing in a part of it they have never
 * seen, which is precisely the search the zones exist to remove.
 */
export function carveZones(
  graph: NavmeshGraph,
  spawn: [number, number, number],
  exit: [number, number, number],
  posts: [number, number, number][],
): Zone[] {
  const g = graph;
  const from = g.locate(spawn[0], spawn[1], spawn[2], 6);
  const to = g.locate(exit[0], exit[1], exit[2], 6);
  const ds = bfs(g, from);
  const de = bfs(g, to);

  const route = from >= 0 && to >= 0 ? ds[to] : -1;
  // A map with no measurable route through it — one sample, or a spawn the
  // bake could not find — is a single zone and the old behaviour.
  if (route <= 2) return [wholeMap(g, posts)];

  /*
   * How many.
   *
   * From the length of the route rather than the area of the map, because what
   * decides whether two places are one fight is how long it takes to walk
   * between them. Three is the usual answer; two for something you cross in
   * twenty seconds, four for the long ones. More than four is not a better
   * floor, it is a longer one, and a run has plenty of length already.
   */
  let want = Math.max(2, Math.min(4, Math.round(route / 105)));

  let reach = 0;
  for (let i = 0; i < g.count; i++) if (ds[i] >= 0 && de[i] >= 0) reach++;

  /*
   * A zone has to be somewhere, not a pinch point.
   *
   * Cutting a map into equal thirds of *route* can put a band entirely inside
   * a corridor — the flow distance advances quickly through somewhere narrow —
   * and a "zone" ten metres across with a fight in it is an ambush in a
   * hallway. So a band that comes out too small takes the whole scheme down a
   * notch and it is cut again, coarser.
   */
  for (; want >= 2; want--) {
    const zones = band(g, ds, de, want);
    const smallest = Math.min(...zones.map((z) => z.nodes.length));
    if (want === 2 || smallest > reach * 0.1) {
      if (!zones.length) break;
      assignPosts(zones, posts);
      return zones;
    }
  }
  return [wholeMap(g, posts)];
}

/** Cut into `want` equal bands of route progress and keep each one's core. */
function band(g: NavmeshGraph, ds: Int32Array, de: Int32Array, want: number): Zone[] {
  const bands: number[][] = Array.from({ length: want }, () => []);
  for (let i = 0; i < g.count; i++) {
    if (ds[i] < 0 || de[i] < 0) continue;
    const total = ds[i] + de[i];
    const flow = total > 0 ? ds[i] / total : 0;
    bands[Math.min(want - 1, Math.floor(flow * want))].push(i);
  }
  const zones: Zone[] = [];
  for (let b = 0; b < want; b++) {
    const nodes = largestPiece(g, bands[b]);
    if (nodes.length < 4) continue;
    zones.push(makeZone(zones.length, g, nodes, (b + 0.5) / want));
  }
  return zones;
}

/** The biggest connected component of a set of samples. */
function largestPiece(g: NavmeshGraph, nodes: number[]): number[] {
  const inSet = new Set(nodes);
  const seen = new Set<number>();
  let best: number[] = [];
  for (const start of nodes) {
    if (seen.has(start)) continue;
    const piece: number[] = [];
    const stack = [start];
    seen.add(start);
    while (stack.length) {
      const u = stack.pop()!;
      piece.push(u);
      for (let k = g.adjOffset[u]; k < g.adjOffset[u + 1]; k++) {
        const v = g.adjList[k];
        if (!inSet.has(v) || seen.has(v)) continue;
        seen.add(v);
        stack.push(v);
      }
    }
    if (piece.length > best.length) best = piece;
  }
  return best;
}

function makeZone(id: number, g: NavmeshGraph, nodes: number[], progress: number): Zone {
  let sx = 0;
  let sy = 0;
  let sz = 0;
  for (const n of nodes) {
    sx += g.points[n * 3];
    sy += g.points[n * 3 + 1];
    sz += g.points[n * 3 + 2];
  }
  const centre: [number, number, number] = [sx / nodes.length, sy / nodes.length, sz / nodes.length];
  let far = 0;
  for (const n of nodes) {
    const d = Math.hypot(g.points[n * 3] - centre[0], g.points[n * 3 + 1] - centre[1]);
    if (d > far) far = d;
  }
  return { id, nodes, centre, radius: Math.max(6, far), heart: openest(g, nodes), progress, posts: [] };
}

/**
 * The spot in a zone with the most floor around it.
 *
 * Samples are bucketed into cells the size of the signal and the fullest cell
 * wins; the answer is then a real sample from that cell, so it is somewhere
 * that can actually be stood on. Elevation is part of the key, so a mezzanine
 * directly over a hall does not add its samples to the hall's count and hand
 * the signal a position halfway between two floors.
 */
function openest(g: NavmeshGraph, nodes: number[]): [number, number, number] {
  const CELL = HOLD_RADIUS;
  const bins = new Map<string, number[]>();
  for (const n of nodes) {
    const k = `${Math.floor(g.points[n * 3] / CELL)},${Math.floor(g.points[n * 3 + 1] / CELL)},${Math.floor(g.points[n * 3 + 2] / 3)}`;
    const bag = bins.get(k);
    if (bag) bag.push(n);
    else bins.set(k, [n]);
  }
  let best: number[] = nodes;
  for (const bag of bins.values()) if (bag.length > best.length || best === nodes) best = bag;

  // The middle of the fullest cell, then the real sample nearest to it.
  let cx = 0;
  let cy = 0;
  let cz = 0;
  for (const n of best) {
    cx += g.points[n * 3];
    cy += g.points[n * 3 + 1];
    cz += g.points[n * 3 + 2];
  }
  cx /= best.length;
  cy /= best.length;
  cz /= best.length;
  let at = best[0];
  let near = Infinity;
  for (const n of best) {
    const d = Math.hypot(g.points[n * 3] - cx, g.points[n * 3 + 1] - cy) + Math.abs(g.points[n * 3 + 2] - cz);
    if (d < near) {
      near = d;
      at = n;
    }
  }
  return [g.points[at * 3], g.points[at * 3 + 1], g.points[at * 3 + 2]];
}

function wholeMap(g: NavmeshGraph, posts: [number, number, number][]): Zone {
  const all: number[] = [];
  for (let i = 0; i < g.count; i++) if (g.reachable[i]) all.push(i);
  const z = makeZone(0, g, all.length ? all : [0], 0.5);
  z.posts = posts.slice();
  return z;
}

/**
 * Every post belongs to exactly one zone, and no zone is left with none.
 *
 * Nearest centre in the plan, with elevation weighted heavily: a post on a
 * gantry directly above a zone is not in it, and a fight that spawns above the
 * ceiling of the room you are standing in is the ambush-from-nowhere the
 * concealment rules exist to prevent.
 */
function assignPosts(zones: Zone[], posts: [number, number, number][]): void {
  for (const p of posts) {
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < zones.length; i++) {
      const c = zones[i].centre;
      const d = Math.hypot(p[0] - c[0], p[1] - c[1]) + Math.abs(p[2] - c[2]) * 2.5;
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    zones[best].posts.push(p);
  }
  // A zone with nowhere to put anything cannot host its own wave, so it
  // borrows from the richest neighbour rather than silently going empty.
  for (const z of zones) {
    if (z.posts.length) continue;
    const richest = zones.slice().sort((a, b) => b.posts.length - a.posts.length)[0];
    if (richest && richest.posts.length > 3) z.posts.push(...richest.posts.splice(0, 2));
  }
}

// -----------------------------------------------------------------------------

/**
 * Which contract this floor gets.
 *
 * Sweep is the common case and the only one floor one and two are allowed,
 * because it is the structured version of what the player already understands.
 * Hunt and hold arrive once there is something to vary, and never twice
 * running: a floor that repeats the previous floor's verb has spent a
 * contract slot on nothing.
 *
 * Deterministic in the depth and the seed, so a shared run is a shared run.
 */
export function contractFor(depth: number, zones: number, rand: () => number, last?: Contract): Contract {
  if (depth <= 2 || zones < 2) return 'sweep';
  const options: Contract[] = ['sweep', 'sweep', 'hunt'];
  if (depth >= 4) options.push('hold');
  if (depth >= 6) options.push('hunt', 'hold');
  const pick = options[Math.floor(rand() * options.length)] ?? 'sweep';
  if (pick === last && pick !== 'sweep') return 'sweep';
  return pick;
}

/**
 * Split the floor's roster between its zones.
 *
 * Weighted toward the far end — the last zone is the one the floor is about,
 * and walking into the biggest fight is a better shape than walking away from
 * it. The split is by cost rather than by count so that a zone holding one
 * brute and a zone holding four runners are the same amount of trouble.
 */
export function shareOut(roster: EnemyType[], zones: number, contract: Contract): EnemyType[][] {
  const waves: EnemyType[][] = Array.from({ length: zones }, () => []);
  if (zones <= 1) {
    waves[0] = roster.slice();
    return waves;
  }
  const weight: number[] = [];
  for (let i = 0; i < zones; i++) weight.push(0.7 + (i / (zones - 1)) * 0.9);
  // A hunt is nearly all in the target's zone; the walk there is a walk.
  if (contract === 'hunt') for (let i = 0; i < zones - 1; i++) weight[i] *= 0.42;
  const sum = weight.reduce((a, b) => a + b, 0);
  const total = roster.reduce((a, t) => a + cost(t), 0);
  const quota = weight.map((w) => (w / sum) * total);

  const spent = new Array(zones).fill(0);
  for (const t of roster) {
    // Whichever zone is furthest below its share takes the next one, which
    // keeps a single expensive type from emptying a zone's whole allowance.
    let best = 0;
    let worst = -Infinity;
    for (let i = 0; i < zones; i++) {
      const room = quota[i] - spent[i];
      if (room > worst) {
        worst = room;
        best = i;
      }
    }
    waves[best].push(t);
    spent[best] += cost(t);
  }

  /*
   * No zone gets nothing.
   *
   * On a shallow floor the whole roster is two or three enemies and the
   * weighting puts all of them in the last zone, which leaves the first one a
   * corridor you walk down — and on floor one it left the *opening* enemy
   * with nothing to be, so a first-time player saw their first hostile
   * fourteen seconds in. A zone with an empty wave is not a zone.
   */
  for (let i = 0; i < zones; i++) {
    if (waves[i].length) continue;
    let from = -1;
    for (let j = 0; j < zones; j++) if (waves[j].length > (from < 0 ? 1 : waves[from].length)) from = j;
    if (from < 0) break;
    waves[i].push(waves[from].pop()!);
  }
  /*
   * And the *first* zone gets one whatever the arithmetic says.
   *
   * A shallow floor's whole roster is two enemies, so there is nothing to
   * spare and the loop above correctly declines to rob a zone that has only
   * one. But zone one is where the player is standing when the floor starts,
   * and on floor one its first enemy is placed there before anybody has
   * moved. An empty first wave meant a first-time player's first hostile
   * arrived fourteen seconds in — measured, which is the only reason anyone
   * noticed.
   */
  if (!waves[0].length) {
    for (let j = 1; j < zones; j++) {
      if (!waves[j].length) continue;
      waves[0].push(waves[j].shift()!);
      break;
    }
  }
  return waves;
}

// -----------------------------------------------------------------------------

/** What the director wants to happen this frame. */
export interface Beat {
  /** Release one enemy from the active zone's wave. */
  release: boolean;
  /** The zone anything released should come from. */
  zone: number;
}

/** Where a zone is in its own arc. */
type Stage = 'dormant' | 'arming' | 'building' | 'peak' | 'clear';

/**
 * The floor's clock.
 *
 * Holds which zone is live, how much of its wave is still owed, and how long
 * the quiet between them lasts. Everything it knows about the player is where
 * they are standing and what is still alive — no health, no accuracy, no
 * performance. It is a metronome with a map, not a difficulty system.
 */
export class Director {
  readonly zones: Zone[];
  readonly contract: Contract;
  readonly waves: EnemyType[][];
  /** The zone whose fight is currently live. */
  active = 0;
  /** For `hunt`: the zone the marked target is in, and which of its wave. */
  readonly markZone: number;
  readonly markIndex: number;
  /** For `hold`: the zone with the signal, and how long it takes. */
  readonly holdZone: number;
  readonly holdSeconds: number;
  /** 0..1, and it only moves while somebody is standing in the signal. */
  holdProgress = 0;
  /** True once the contract is satisfied and the way out should open. */
  done = false;
  /** Set when the marked target dies. */
  markDown = false;

  private stage: Stage = 'dormant';
  private timer = 0;
  /** Seconds the floor has spent making no progress at all. */
  private waiting = 0;
  /** Seconds with every wave released and nothing standing. */
  private empty = 0;
  private released = 0;
  /**
   * How many of the ones it released are still on their feet.
   *
   * Told to it every frame rather than counted by it. A tally the director
   * kept for itself would be right only for as long as every removal path in
   * the run remembered to decrement it, and a tally that drifts upward by one
   * is a floor that can never be finished — the worst failure this system can
   * have, and one the player cannot see or work around. The run knows exactly
   * what is standing; asking it is free.
   */
  private standing = 0;

  constructor(zones: Zone[], contract: Contract, waves: EnemyType[][], depth: number) {
    this.zones = zones;
    this.contract = contract;
    this.waves = waves;
    this.markZone = zones.length - 1;
    /*
     * The biggest thing in the last zone carries the mark.
     *
     * Not a new type. The player already knows what a Brute does; a Brute they
     * have been told to kill is a fight they can plan, where a boss with rules
     * nobody has seen before is a fight they can only survive and then have to
     * learn afterwards. Marking the toughest thing present also means the hunt
     * is always the hardest single fight the floor was going to give them
     * anyway, which is what makes it feel like the point of the floor rather
     * than a detour from it.
     */
    const last = waves[this.markZone] ?? [];
    let at = 0;
    for (let i = 1; i < last.length; i++) if (cost(last[i]) > cost(last[at])) at = i;
    this.markIndex = at;
    // Not the last zone: a hold you have to walk back out of afterwards is a
    // hold with a retreat, and the retreat is half of what makes it a fight.
    this.holdZone = Math.max(0, Math.min(zones.length - 1, zones.length - 2));
    this.holdSeconds = Math.min(35, 20 + depth * 0.8);
  }

  /** How many are owed from the active zone but not yet out. */
  get owed(): number {
    return (this.waves[this.active]?.length ?? 0) - this.released;
  }

  /** Everything the floor still has for you, released or not. */
  get remaining(): number {
    let left = 0;
    for (let i = this.active; i < this.waves.length; i++) {
      left += this.waves[i].length - (i === this.active ? this.released : 0);
    }
    return left + this.standing;
  }

  /**
   * Is the next release the floor's stand-out one?
   *
   * Separate from `nextIsMark` because a hunt is not the only reason to single
   * something out: an elite is a rule laid over the toughest thing on the
   * floor whatever the contract, and this is where that thing comes out.
   */
  get atMarkSlot(): boolean {
    return this.active === this.markZone && this.released === this.markIndex;
  }

  /** Is the next one out the one the floor is about? */
  get nextIsMark(): boolean {
    return this.contract === 'hunt'
      && this.active === this.markZone
      && this.released === this.markIndex
      && !this.markDown;
  }

  /** The next type owed by the active zone, without taking it. */
  peek(): EnemyType | undefined {
    return this.waves[this.active]?.[this.released];
  }

  /** Take it. */
  take(): EnemyType | undefined {
    const t = this.peek();
    if (t) this.released++;
    return t;
  }

  /** Where the signal is, for the marker and the HUD. */
  get signal(): [number, number, number] | null {
    return this.contract === 'hold' ? this.zones[this.holdZone]?.heart ?? null : null;
  }

  /**
   * Advance the floor.
   *
   * `at` is the player in canonical coordinates. The return says only whether
   * something should come out this frame and where from; the run owns the
   * actual spawning, because only it knows what the player can see.
   */
  update(dt: number, at: [number, number, number], grace: number, standing: number): Beat {
    this.standing = standing;
    const quiet: Beat = { release: false, zone: this.active };
    if (this.done) return quiet;

    const zone = this.zones[this.active];
    /*
     * A zone arms when you reach it *or* when you have gone past it.
     *
     * The second half is not a nicety, it is the difference between a floor
     * that ends and one that does not. A player who runs the length of the map
     * without stopping is standing in the last zone while the second is still
     * asleep, and a second zone that never arms is a floor whose contract can
     * never be satisfied — a deadlock the player has no way to see, let alone
     * undo. So the test is against the furthest zone they have got to rather
     * than the one they are in.
     */
    const where = this.nearestZone(at);
    const near = zone ? where >= this.active || this.inside(zone, at) : true;

    if (this.contract === 'hold') this.tickHold(dt, at);
    /*
     * A hunt ends the moment the target falls, not when the zone finishes.
     *
     * "Kill it and go" has to mean go. Waiting for the rest of the wave to
     * finish arriving, and then for those to die, turns the contract back into
     * the sweep it exists to be an alternative to — and it is the one thing
     * about a hunt the player is told in advance.
     */
    if (this.contract === 'hunt' && this.markDown) {
      this.done = true;
      return quiet;
    }

    /*
     * And a watchdog, because a stalled floor is unrecoverable.
     *
     * Everything above is a rule about where the player is, and every rule
     * about where the player is has a case where they are somewhere the rule
     * did not consider — off the mesh, on a roof, wedged in a stairwell. If a
     * zone has been waiting long enough that nothing can be happening, it goes
     * ahead without them.
     */
    this.waiting = this.stage === 'dormant' || (this.stage === 'peak' && this.standing === 0)
      ? this.waiting + dt
      : 0;
    this.empty = this.active >= this.zones.length - 1 && this.owed <= 0 && standing === 0
      ? this.empty + dt
      : 0;

    switch (this.stage) {
      case 'dormant':
        if (near || this.active === 0 || this.waiting > STALLED) {
          this.stage = 'arming';
          this.timer = this.active === 0 ? grace : 0.9;
          this.waiting = 0;
        }
        return quiet;

      case 'arming':
        this.timer -= dt;
        if (this.timer <= 0) {
          this.stage = 'building';
          this.timer = 0;
        }
        return quiet;

      case 'building': {
        this.timer -= dt;
        if (this.timer > 0) return quiet;
        if (this.owed <= 0) {
          this.stage = 'peak';
          return quiet;
        }
        /*
         * The interval shortens as the zone fills.
         *
         * A constant drip is a queue, and a queue is fought one enemy at a
         * time no matter how many are in it. Opening slowly and then
         * accelerating gives the zone a shape — you meet the fight, then the
         * fight arrives — and it is the same curve every time, so it can be
         * anticipated rather than merely endured.
         */
        const through = this.released / Math.max(1, this.waves[this.active].length);
        this.timer = Math.max(0.28, 1.5 - through * 1.1);
        return { release: true, zone: this.active };
      }

      case 'peak':
        if (this.standing > 0) return quiet;
        this.stage = 'clear';
        // A breath. Long enough to reload and look at where you are, short
        // enough that it is a beat rather than an intermission.
        this.timer = 2.4;
        return quiet;

      case 'clear':
        this.timer -= dt;
        if (this.timer > 0) return quiet;
        if (this.satisfied()) {
          this.done = true;
          return quiet;
        }
        if (this.active < this.zones.length - 1) {
          this.active++;
          this.released = 0;
          this.stage = 'dormant';
        } else {
          // Nothing left to arm and the contract is not met: for a hold that
          // is simply the signal still running, and the zone stays open so
          // anything still owed can keep coming.
          this.stage = 'peak';
        }
        return quiet;
    }
  }

  /**
   * Has the floor's contract been met?
   *
   * Every branch has the same escape hatch under it: a floor with every wave
   * released and nothing left standing has given the player everything it
   * had, and after that there is nothing they can do that will change the
   * answer. A hunt whose mark zone drew an empty wave — possible with a small
   * roster and one expensive type — could otherwise never be satisfied at all,
   * which is a run ending in a room with nothing to do and no way to say so.
   */
  private satisfied(): boolean {
    const spent = this.active >= this.zones.length - 1 && this.owed <= 0 && this.standing === 0;
    if (this.contract === 'hunt') return this.markDown || spent;
    if (this.contract === 'hold') {
      // A hold is the one contract the player can still complete with the
      // floor empty — walk into the ring — so it gets a much longer rope
      // before the floor gives it to them.
      return this.holdProgress >= 1 || (spent && this.empty > 60);
    }
    return spent;
  }

  private tickHold(dt: number, at: [number, number, number]): void {
    const z = this.zones[this.holdZone];
    if (!z) return;
    const d = Math.hypot(at[0] - z.heart[0], at[1] - z.heart[1]);
    const height = Math.abs(at[2] - z.heart[2]);
    const inSignal = d < HOLD_RADIUS && height < 4;
    if (inSignal) {
      this.holdProgress = Math.min(1, this.holdProgress + dt / this.holdSeconds);
    } else {
      // Leaving pauses and slowly gives back rather than failing outright. A
      // hold you can lose by being pushed out of it once is a hold that
      // punishes the correct answer to being overwhelmed.
      this.holdProgress = Math.max(0, this.holdProgress - dt / (this.holdSeconds * 4));
    }
    if (this.holdProgress >= 1) this.done = true;
  }

  /**
   * Which zone the player is furthest into.
   *
   * By centre, with elevation weighted, and never null: a point that is
   * nowhere near any of them still has a nearest one, which is exactly what
   * the arming rule needs so that it cannot fail to have an answer.
   */
  private nearestZone(at: [number, number, number]): number {
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < this.zones.length; i++) {
      const c = this.zones[i].centre;
      const d = Math.hypot(at[0] - c[0], at[1] - c[1]) + Math.abs(at[2] - c[2]) * 2;
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best;
  }

  private inside(z: Zone, at: [number, number, number]): boolean {
    const d = Math.hypot(at[0] - z.centre[0], at[1] - z.centre[1]);
    return d < z.radius + 5 && Math.abs(at[2] - z.centre[2]) < 6;
  }
}

/**
 * Seconds of a floor visibly doing nothing before it stops asking permission.
 *
 * Long enough that it never fires during ordinary play — a zone arms the
 * moment you walk toward it, and the walk between two of them is seconds —
 * and short enough that a player who has found a way to confuse it is
 * inconvenienced rather than stranded.
 */
const STALLED = 12;
